import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import SimpleButton from "../components/Buttons/SimpleButton";
import { DragAndDrop } from "../components/DragAndDrop/DragAndDrop";
import { NavBar } from "../components/NavBar";
import Text from "../components/Text/Text";
import { Timeline } from "../components/Timeline/Timeline";
import { useAuth } from "../hooks/useAuth";
import uploading_state from "../imgs/uploading_cloud.svg";
import upload_cloud from "../imgs/upload_cloud.svg";
import upload_cloud_error from "../imgs/upload_cloud_error.svg";
import upload_cloud_success from "../imgs/upload_cloud_success.svg";

interface UploadFileProps {
  theme: string;
  toggleTheme: () => void;
}

const UploadFilePageContainer = styled.div`
  display: flex;
  background: #000d30;
  background-size: cover;
  width: 99vw;
  height: 100vh;
  justify-content: center;
  align-items: center;
  position: relative;
  padding-top: 3%;
`;

const UploadContainer = styled("label")<{ state: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 50%;
  padding-top: 50px;
  padding-bottom: 50px;
  padding-inline: 150px;
  border-radius: 30px;
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 30px;
    padding: 2px;
    z-index: -1;

    ${({ state }) =>
      state === "normal" &&
      `
      border: 0.8px solid #FFFFFF90;
      backdrop-filter: blur(61.9333px);
    `}

    ${({ state }) =>
      state === "dragged" &&
      `
    background: none;
    border: 2px dashed;
    z-index:2;

    `}

    ${({ state }) =>
      state === "error" &&
      `
    background: linear-gradient(-90deg, #FF3535, #FA00FF);
    `}

    ${({ state }) =>
      state === "success" &&
      `
    background: linear-gradient(-90deg, #0085FF -65% , #51F019);
    `}
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  &:hover {
    cursor: pointer;
  }
`;

const NextButton = styled(SimpleButton)`
  align-self: flex-end;
  margin-top: 120px;
  width: 20%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 27%;

  @media screen and (max-height: 1080px) {
    margin-top: 10%;
    min-width: 40%;
  }
`;

const DisabledButton = styled(SimpleButton)`
  width: 20%;
  align-self: flex-end;
  margin-top: 120px;

  @media screen and (max-height: 1080px) {
  margin-right: -5%;
  }
`;

interface UploadFileState {
  masterCopy: boolean;
}

export const UploadFilePage: React.FC<UploadFileProps> = ({
  theme,
  toggleTheme,
}) => {
  const location = useLocation();
  const locationState = location.state as UploadFileState;
  const auth = useAuth();
  const [state, setState] = useState("normal");
  const [displayMessage, setDisplayMessage] = useState(
    "Click or Drop your File to Upload"
  );
  const [uploadIcon, setUploadIcon] = useState(upload_cloud);
  const timelineEvents = locationState.masterCopy
    ? [
        "Upload Music",
        "Enter Details",
        "Terms and Conditions",
        "List Options",
        "Set a Time",
      ]
    : ["Upload Music", "Enter Details", "List Options", "Set a Time"];
  const navigate = useNavigate();
  const [song, setSong] = useState<File>();

  useEffect(() => {
    if (auth.user.stage_name === null || auth.user.stage_name === "") {
      navigate("/artist_info", { replace: true });
    }
  }, [auth.user]);
  const onFileDrop = (files: FileList) => {
    if (files.length < 0) {
      return;
    }

    setSong(files[0]);
    setState("normal");
    setDisplayMessage("Checking...");
    setUploadIcon(uploading_state);
    var formData = new FormData();
    formData.append("song", files[0]);
    formData.append("song_name", files[0].name);

    formData.append("artist_name", auth.user.stage_name);
    formData.append("is_master_copy", locationState.masterCopy.toString());
    axios
      .post(process.env.REACT_APP_BACKEND_URL + "/song_exists", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (!response.data["exists"]) {
          setState("success");
          setUploadIcon(upload_cloud_success);
          setDisplayMessage("Your file has been successfully uploaded!");
        } else {
          setDisplayMessage(response.data["message"]);
          setState("error");
          setUploadIcon(upload_cloud_error);
        }
      })
      .catch(() => {
        setState("error");
        setDisplayMessage("Something went wrong, please try again");
      });

    // setState("success");
    // setUploadIcon(upload_cloud_success);
    // setDisplayMessage("Thanks! Please click on Next");
  };

  const onNextClick = () => {
    navigate("/upload_song_details", {
      state: {
        songFile: song,
        timelineEvents: timelineEvents,
        masterCopy: locationState.masterCopy,
      },
    });
  };

  const onDragIn = () => {
    setState("dragged");
  };

  const onDragOut = () => {
    setState("normal");
  };

  const onSelectedFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSong(event.target.files[0]);
      setState("normal");
      setDisplayMessage("Checking...");
      setUploadIcon(uploading_state);
      var formData = new FormData();
      formData.append("song", event.target.files[0]);
      formData.append("song_name", event.target.files[0].name);

      formData.append("artist_name", auth.user.stage_name);
      formData.append("is_master_copy", locationState.masterCopy.toString());
      axios
        .post(process.env.REACT_APP_BACKEND_URL + "/song_exists", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (!response.data["exists"]) {
            setState("success");
            setUploadIcon(upload_cloud_success);
            setDisplayMessage("Your file has been successfully uploaded!");
          } else {
            setDisplayMessage(response.data["message"]);
            setState("error");
            setUploadIcon(upload_cloud_error);
          }
        })
        .catch(() => {
          setState("error");
          setDisplayMessage("Something went wrong, please try again");
        });
    }
  };

  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} hideNavLinks={true} />
      <UploadFilePageContainer>
        <Timeline events={timelineEvents} currentEventIndex={0} header={
          locationState.masterCopy ? "Creating Master Copy NFT": "Creating Collectible NFT"}/>
        <Container>
          <DragAndDrop
            handleDropParent={onFileDrop}
            onDragIn={onDragIn}
            onDragOut={onDragOut}
          >
            <UploadContainer state={state}>
              <input
                style={{ display: "none" }}
                accept=".mp3,audio/*"
                id="upload_file"
                type="file"
                onChange={onSelectedFile}
              />
              <img style={{ width: "150px" }} src={uploadIcon} />
              <Text style={{minWidth: '80%', maxWidth: '80%', textAlign: 'center'}}>{displayMessage}</Text>
            </UploadContainer>
          </DragAndDrop>
          {state == "success" ? (
            <NextButton onClick={onNextClick}>Next</NextButton>
          ) : (
            <DisabledButton bg="primary" disabled>
              Next
            </DisabledButton>
          )}
        </Container>
      </UploadFilePageContainer>
    </>
  );
};
